import * as TabsPrimitive from '@radix-ui/react-tabs';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FC, ReactElement, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, Outlet } from 'react-router-dom';
import { cn, FaIcon, Skeleton } from '@fe-learner-common';
import { useBreakpoint } from '@fe-learner-common/hooks/useBreakpoint';

export type Tab = {
  titleId: string;
  value: string;
  icon: IconDefinition;
  reloadOnClick?: boolean;
};

type TabContent = {
  triggers: ReactElement[];
  content: ReactElement[];
};

type TabsProps = {
  tabs: Tab[];
  isLoading?: boolean;
  selectedTab?: string;
  onValueChange?: (value: string) => void;
};

const TriggerSkeleton: FC = () => {
  return (
    <>
      <Skeleton type="button" className="lf:w-full" />
      <Skeleton type="button" className="lf:w-full" />
      <Skeleton type="button" className="lf:w-full" />
      <Skeleton type="button" className="lf:w-full" />
    </>
  );
};

/*
 * A component that renders a tabbed view with a list of tabs and their content.
 *
 * @state - index - The only use is to update the key of the triggers and content, to support the functionality of re-rendering a tab when if the trigger is clicked when already active.
 * */
export const Tabs: FC<TabsProps> = ({
  selectedTab,
  tabs,
  isLoading,
  onValueChange,
}) => {
  const [index, setIndex] = useState(0);
  const selectedRef = useRef<string | null>(null);
  const intl = useIntl();
  const matchesLg = useBreakpoint('lg');

  const tabContent = useMemo<TabContent | null>(() => {
    if (!tabs.length) return null;

    return tabs.reduce(
      (acc, cur) => {
        if (cur) {
          const trigger = (
            <TabsPrimitive.Trigger
              asChild
              key={`course-info-tab-${cur.value}-trigger-${
                cur?.reloadOnClick ? index : ''
              }`}
              autoFocus={selectedRef.current === cur.value}
              value={cur.value}
              onClick={() => {
                setIndex((prev) => prev + 1);
                selectedRef.current = cur.value;
              }}
              className="lf:flex lf:items-center lf:gap-3 lf:rounded-lg lf:px-4 lf:py-2.5 lf:text-label-lg lf:hover:bg-neutral-100 lf:hover:text-neutral-900 lf:data-[state=active]:bg-neutral-100 lf:data-[state=active]:text-neutral-900"
            >
              <Link to={cur.value}>
                <FaIcon width={20} icon={cur.icon} />
                <span>{intl.formatMessage({ id: cur.titleId })}</span>
              </Link>
            </TabsPrimitive.Trigger>
          );

          const content = (
            <TabsPrimitive.Content
              value={cur.value}
              asChild
              key={`course-info-tab-${cur.value}-content-${
                cur?.reloadOnClick ? index : ''
              }`}
            >
              <section className="lf:flex lf:flex-1 lf:flex-col lf:gap-y-6 lf:px-3 lf:pb-6 lf:md:gap-y-4 lf:md:pb-0 lf:lg:px-0">
                <Outlet />
              </section>
            </TabsPrimitive.Content>
          );

          return {
            triggers: [...acc.triggers, trigger],
            content: [...acc.content, content],
          };
        }

        return acc;
      },
      { triggers: [], content: [] },
    );
  }, [tabs, index, intl]);

  if (!tabContent) return null;
  return (
    <div className="lf:lg:l-page lf:flex-1 lf:2xl:container">
      <TabsPrimitive.Root
        orientation={matchesLg ? 'vertical' : 'horizontal'}
        className="lf:flex lf:flex-col lf:gap-8 lf:lg:flex-row lf:lg:gap-x-16"
        activationMode="manual"
        value={selectedTab}
        onValueChange={(value) => {
          onValueChange?.(value);
        }}
      >
        <TabsPrimitive.List
          aria-label="Course information"
          className={cn(
            'lf:flex lf:w-full lf:flex-row lf:gap-3 lf:overflow-x-auto lf:px-6 lf:py-4 lf:text-neutral-500',
            'lf:lg:mx-3 lf:lg:w-[320px] lf:lg:min-w-[320px] lf:lg:flex-col lf:lg:gap-2 lf:lg:overflow-visible lf:lg:p-0',
          )}
        >
          {isLoading ? <TriggerSkeleton /> : tabContent.triggers}
        </TabsPrimitive.List>

        {tabContent.content}
      </TabsPrimitive.Root>
    </div>
  );
};
